import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

function formatPriceFull(p) {
  if (!p) return null;
  return '$' + p.toLocaleString();
}

const statusBadge = {
  pending: 'badge-blue',
  'under-review': 'badge-blue',
  countered: 'badge-purple',
  accepted: 'badge-green',
  declined: 'badge-gray',
  withdrawn: 'badge-gray',
};

const statusLabel = {
  pending: 'submitted',
  'under-review': 'under review',
  countered: 'counter received',
  accepted: 'accepted',
  declined: 'declined',
  withdrawn: 'withdrawn',
};

export default function BuyerDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user]);

  async function fetchData() {
    setLoading(true);

    // Fetch bids with property details
    const { data: bidsData } = await supabase
      .from('bids')
      .select(`
        *,
        properties (
          id, address, city, state, zip,
          beds, baths, sqft, asking_price, zestimate, status
        )
      `)
      .eq('buyer_id', user.id)
      .neq('status', 'withdrawn')
      .order('created_at', { ascending: false });

    // Fetch notifications
    const { data: notifsData } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    setBids(bidsData || []);
    setNotifications(notifsData || []);
    setLoading(false);
  }

  async function withdrawBid(bidId) {
    const { error } = await supabase
      .from('bids')
      .update({ status: 'withdrawn' })
      .eq('id', bidId)
      .eq('buyer_id', user.id);

    if (!error) fetchData();
  }

  if (!user) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Please sign in to view your bids.</p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>go home</button>
    </div>
  );

  const activeBids = bids.filter(b => ['pending', 'under-review', 'countered'].includes(b.status));
  const actionRequired = bids.filter(b => b.status === 'countered');

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600 }}>
            welcome back{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Houston, TX · buyer account</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/search')}>browse homes</button>
      </div>

      {/* Metrics */}
      <div className="metric-grid">
        <div className="metric"><div className="val">{activeBids.length}</div><div className="lbl">active bids</div></div>
        <div className="metric"><div className="val">{actionRequired.length}</div><div className="lbl">counter received</div></div>
        <div className="metric"><div className="val">{bids.length}</div><div className="lbl">total bids placed</div></div>
        <div className="metric"><div className="val">{notifications.filter(n => !n.read).length}</div><div className="lbl">unread notifications</div></div>
      </div>

      <div className="two-col">
        <div>
          {/* Action required */}
          {actionRequired.length > 0 && (
            <div className="card" style={{ marginBottom: 16, border: '1px solid var(--blue-mid)' }}>
              <h3 style={{ marginBottom: 14 }}>bids requiring action</h3>
              {actionRequired.map(bid => (
                <div className="row-item" key={bid.id}>
                  <div style={{ flex: 1 }}>
                    <div className="row-title">{bid.properties?.address}</div>
                    <div className="row-sub">counter-offer received · {bid.counter_amount ? `$${bid.counter_amount.toLocaleString()}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: 'var(--purple)' }}>
                      {bid.counter_amount ? formatPriceFull(bid.counter_amount) : ''}
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ marginTop: 6 }}
                      onClick={() => navigate(`/bid/${bid.property_id}`)}
                    >
                      review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All bids */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 14 }}>
              {loading ? 'loading...' : `all active bids (${activeBids.length})`}
            </h3>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>loading your bids...</div>
            ) : bids.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 14 }}>You haven't placed any bids yet.</p>
                <button className="btn btn-primary" onClick={() => navigate('/search')}>browse homes</button>
              </div>
            ) : (
              bids.map(bid => (
                <div className="row-item" key={bid.id}>
                  <div style={{ flex: 1 }}>
                    <div className="row-title">{bid.properties?.address}</div>
                    <div className="row-sub">
                      {bid.properties?.beds} bd · {bid.properties?.baths} ba · {bid.closing_timeline}-day close
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{formatPriceFull(bid.amount)}</div>
                    <span className={`badge ${statusBadge[bid.status] || 'badge-gray'}`} style={{ marginTop: 4, display: 'inline-block' }}>
                      {statusLabel[bid.status] || bid.status}
                    </span>
                    {['pending', 'under-review'].includes(bid.status) && (
                      <div>
                        <button
                          className="btn btn-sm"
                          style={{ marginTop: 5, fontSize: 11, color: 'var(--text-tertiary)' }}
                          onClick={() => withdrawBid(bid.id)}
                        >
                          withdraw
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Letter service promo */}
          {bids.length > 0 && (
            <div className="card" style={{ background: 'var(--blue-light)', border: '1px solid var(--blue-mid)' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20 }}>✉</span>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--blue-dark)', marginBottom: 4 }}>
                    Want to stand out? Send the owner a letter.
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--blue-dark)', lineHeight: 1.6, marginBottom: 12 }}>
                    Homes4Bid mails a physical letter to the homeowner on your behalf. Starting at $9.
                  </p>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/letter-service')}>
                    learn more
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          {/* Personalized shortlist teaser */}
          <div className="card" style={{ marginBottom: 14, background: 'var(--blue-light)', border: '1px solid var(--blue-mid)' }}>
            <h3 style={{ marginBottom: 6, color: 'var(--blue-dark)' }}>for you</h3>
            <p style={{ fontSize: 13, color: 'var(--blue-dark)', lineHeight: 1.6, marginBottom: 12 }}>
              Homes picked to match your saved preferences — timeline, budget, and must-haves.
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/shortlist')}>view for you →</button>
          </div>

          {/* Notifications */}
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>recent notifications</h3>
            {notifications.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0' }}>
                No notifications yet. You'll hear here when owners respond to your bids.
              </div>
            ) : (
              notifications.map((n, i) => (
                <div className="row-item" key={i}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: n.read ? 'var(--border)' : '#E24B4A',
                    flexShrink: 0
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{n.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.body}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {new Date(n.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Search preferences */}
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 10 }}>search preferences</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 13, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>notification method</span>
                <span style={{ color: 'var(--blue)', fontWeight: 500 }}>{profile?.notification_method || 'email'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>account type</span>
                <span style={{ fontWeight: 500 }}>{profile?.role || 'buyer'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
                <span style={{ color: 'var(--text-secondary)' }}>phone</span>
                <span style={{ fontWeight: 500 }}>{profile?.phone || 'not set'}</span>
              </div>
            </div>
            <button className="btn btn-full" style={{ fontSize: 12, marginBottom: 8 }} onClick={() => navigate('/account')}>
              edit account settings
            </button>
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 10 }}>quick links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-full" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => navigate('/search')}>browse homes →</button>
              <button className="btn btn-full" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => navigate('/letter-service')}>letter service →</button>
              <button className="btn btn-full" style={{ fontSize: 12, justifyContent: 'flex-start' }} onClick={() => navigate('/faq')}>owner FAQ →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
